import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { isGmailReconnectError } from './GmailReconnectBanner';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import {
  Mail, Search, Inbox, Star, Send, FileText, Trash2, Archive, RefreshCw,
  Paperclip, X, ChevronLeft, Loader2, AlertCircle, MailOpen, Reply, Forward, Plus, Users,
  UserCheck, Clock as ClockIcon, CheckCircle2, MessageCircle as MsgIcon, Tag,
  PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from './ui/resizable';
import ComposeModal from './ComposeModal';
import InlineReplyComposer from './InlineReplyComposer';
import ThreadCollaboration from './ThreadCollaboration';
import { useAuth } from '../context/AuthContext';
import { useRealtime, useDesktopNotifications } from '../hooks/useRealtime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Built-in folder filters mapped to Gmail label IDs.
const FOLDERS = [
  { id: 'INBOX', label: 'Inbox', icon: Inbox },
  { id: 'STARRED', label: 'Starred', icon: Star },
  { id: 'SENT', label: 'Sent', icon: Send },
  { id: 'DRAFT', label: 'Drafts', icon: FileText },
  { id: 'TRASH', label: 'Trash', icon: Trash2 },
];

// Gmail-style colored avatar (initial + brand color) derived from the sender.
const AVATAR_COLORS = ['#EA4335', '#34A853', '#F9AB00', '#4285F4', '#8E24AA', '#00ACC1', '#E67C73', '#F06292'];
function avatarFor(nameOrEmail) {
  const s = (nameOrEmail || '?').trim();
  const initial = (s[0] || '?').toUpperCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % AVATAR_COLORS.length;
  return { initial, color: AVATAR_COLORS[Math.abs(h)] };
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const sameYear = d.getFullYear() === now.getFullYear();
    if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (sameYear) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function fullDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function ThreadRow({ thread, active, onClick, collab }) {
  const status = collab?.status;
  const assignedTo = collab?.assigned_to;
  const noteCount = collab?.note_count || 0;
  const av = avatarFor(thread.from_name || thread.from_email);
  return (
    <button
      onClick={onClick}
      draggable
      onDragStart={(e) => {
        window.__dragThreadId = thread.id;
        e.dataTransfer.effectAllowed = 'move';
        // Some browsers require setData
        try { e.dataTransfer.setData('text/plain', thread.id); } catch {}
      }}
      onDragEnd={() => { window.__dragThreadId = null; }}
      data-testid={`thread-row-${thread.id}`}
      className={`w-full text-left pl-3 pr-4 py-2.5 border-b border-gray-100 transition-colors cursor-move ${
        active ? 'bg-[#C2DBFF]' : 'bg-white hover:bg-[#F2F6FC]'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
          style={{ backgroundColor: av.color }}
          aria-hidden
        >
          {av.initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className={`text-sm truncate ${thread.is_unread ? 'font-bold text-[#1f1f1f]' : 'text-[#5f6368]'}`}>
              {thread.from_name || thread.from_email || '(unknown)'}
            </span>
            <span className={`text-xs flex-shrink-0 ${thread.is_unread ? 'font-semibold text-[#1f1f1f]' : 'text-[#5f6368]'}`}>
              {formatDate(thread.date)}
            </span>
          </div>
          <div className={`text-sm truncate ${thread.is_unread ? 'font-semibold text-[#1f1f1f]' : 'text-[#444746]'}`}>
            {thread.subject || '(no subject)'}
            {thread.message_count > 1 && (
              <span className="ml-1 text-xs text-[#5f6368]">({thread.message_count})</span>
            )}
            {thread.has_attachment && (
              <Paperclip size={12} className="inline ml-1 -mt-0.5 text-[#5f6368]" />
            )}
          </div>
          <div className="text-xs text-[#5f6368] truncate mt-0.5">
            {thread.has_draft && (
              <span className="text-red-600 font-semibold mr-1" data-testid={`thread-draft-badge-${thread.id}`}>Draft</span>
            )}
            {thread.has_draft && thread.draft_snippet ? thread.draft_snippet : thread.snippet}
          </div>
          {(assignedTo || status === 'snoozed' || status === 'resolved' || noteCount > 0) && (
            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
              {assignedTo && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold">
                  <UserCheck size={10} />
                  {assignedTo.name}
                </span>
              )}
              {status === 'snoozed' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold">
                  <ClockIcon size={10} />
                  Snoozed
                </span>
              )}
              {status === 'resolved' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
                  <CheckCircle2 size={10} />
                  Resolved
                </span>
              )}
              {noteCount > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold">
                  <MsgIcon size={10} />
                  {noteCount}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function MessageBody({ message }) {
  const safeHtml = useMemo(() => {
    if (!message.html) return null;
    return DOMPurify.sanitize(message.html, {
      USE_PROFILES: { html: true },
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel', 'style', 'class',
        'width', 'height', 'colspan', 'rowspan', 'border', 'cellpadding', 'cellspacing',
        'align', 'valign', 'bgcolor'],
      ADD_ATTR: ['target'],
    });
  }, [message.html]);

  if (safeHtml) {
    return (
      <div
        className="prose prose-sm max-w-none [&_a]:text-primary [&_a]:underline"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  }
  if (message.text) {
    return <pre className="whitespace-pre-wrap font-sans text-sm">{message.text}</pre>;
  }
  return <p className="text-sm text-muted-foreground italic">(no body)</p>;
}

function MessageBlock({ message, onDownloadAttachment }) {
  const [expanded, setExpanded] = useState(true);
  const av = avatarFor(message.from_name || message.from_email);
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden bg-white" data-testid={`message-block-${message.id}`}>
      <button
        className="w-full text-left px-4 py-3 hover:bg-[#F6F8FC] flex items-start gap-3"
        onClick={() => setExpanded(v => !v)}
      >
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
          style={{ backgroundColor: av.color }}
          aria-hidden
        >
          {av.initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[#1f1f1f] truncate">{message.from_name || message.from_email}</div>
          <div className="text-xs text-[#5f6368] truncate">
            <span className="font-mono">{message.from_email}</span>
            {message.to && <> → {message.to}</>}
          </div>
        </div>
        <div className="text-xs text-[#5f6368] flex-shrink-0 text-right">
          <div>{fullDate(message.date)}</div>
          {message.is_unread && (
            <span className="inline-block mt-1 px-1.5 py-0.5 rounded-full bg-[#0B57D0]/10 text-[#0B57D0] text-[10px] font-semibold">
              UNREAD
            </span>
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-1 bg-white">
          <MessageBody message={message} />
          {message.attachments?.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Attachments ({message.attachments.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {message.attachments.map(att => (
                  <Button
                    key={att.attachment_id}
                    variant="outline"
                    size="sm"
                    onClick={() => onDownloadAttachment(message.id, att)}
                    data-testid={`attachment-${att.attachment_id}`}
                    className="gap-2"
                  >
                    <Paperclip size={14} />
                    <span className="text-xs">{att.filename}</span>
                    <span className="text-[10px] text-muted-foreground">
                      ({Math.round(att.size / 1024)} KB)
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CommunicationsInbox({ onDisconnect, senderEmail = '', mailboxes = [{ id: 'shared', label: 'Shared (admin@)', email: '' }] }) {
  const { user } = useAuth();
  const [activeMailbox, setActiveMailbox] = useState(mailboxes[0]?.id || 'shared');
  const [folder, setFolder] = useState('INBOX');
  const [labels, setLabels] = useState([]);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [showLabelMenu, setShowLabelMenu] = useState(false);
  const [collabFilter, setCollabFilter] = useState('all'); // 'all' | 'mine' | 'unassigned' | 'open' | 'snoozed' | 'resolved'
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [threads, setThreads] = useState([]);
  const [collabById, setCollabById] = useState({});
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [acting, setActing] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState('new');
  const [composeReplyTo, setComposeReplyTo] = useState(null);
  const [replyState, setReplyState] = useState({ open: false, mode: 'reply' }); // inline composer
  const [threadDraft, setThreadDraft] = useState(null); // existing Gmail draft for the open thread
  const pollRef = useRef(null);

  const openInlineReply = (mode) => {
    setThreadDraft(null);
    setReplyState({ open: true, mode });
  };

  const openCompose = (mode, replyTo = null) => {
    setComposeMode(mode);
    setComposeReplyTo(replyTo);
    setComposeOpen(true);
  };

  // Resolve the current mailbox's email for reply-all "exclude self" logic
  const activeMailboxEmail = useMemo(() => {
    const m = mailboxes.find(mb => mb.id === activeMailbox);
    return m?.email || senderEmail || '';
  }, [activeMailbox, mailboxes, senderEmail]);

  // Shared-mailbox collab features only apply when viewing the shared inbox.
  const showCollab = activeMailbox === 'shared';

  // Fetch labels for the active mailbox
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/communications/labels?mailbox=${activeMailbox}`);
        if (!cancelled) setLabels(res.data?.labels || []);
      } catch (e) {
        if (!cancelled) setLabels([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeMailbox]);

  // User-defined labels only (skip system labels — those are in FOLDERS rail)
  const userLabels = useMemo(
    () => labels.filter(l => l.type === 'user').sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [labels]
  );

  // Apply collab filter (only when showing shared mailbox).
  const visibleThreads = useMemo(() => {
    if (!showCollab || collabFilter === 'all') return threads;
    const myId = user?.id;
    return threads.filter(t => {
      const c = collabById[t.id] || {};
      switch (collabFilter) {
        case 'mine': return c.assigned_to?.id === myId;
        case 'unassigned': return !c.assigned_to;
        case 'open': return (c.status || 'open') === 'open';
        case 'snoozed': return c.status === 'snoozed';
        case 'resolved': return c.status === 'resolved';
        default: return true;
      }
    });
  }, [threads, collabById, collabFilter, showCollab, user]);

  const fetchThreads = useCallback(async (opts = {}) => {
    setLoadingThreads(true);
    try {
      const params = new URLSearchParams();
      params.set('max_results', '30');
      params.set('mailbox', activeMailbox);
      if (folder && folder !== 'ALL') params.set('label', folder);
      if (query) params.set('q', query);
      const res = await axios.get(`${API}/communications/threads?${params}`);
      const list = res.data.threads || [];
      setThreads(list);
      if (res.data.partial) toast.info('Gmail is rate-limiting — showing the threads loaded so far. Refresh in a minute for the rest.', { id: 'gmail-partial' });

      // Hydrate collaboration metadata in a single batch call (shared mailbox only).
      if (activeMailbox === 'shared' && list.length > 0) {
        try {
          const collabRes = await axios.post(`${API}/communications/collab/bulk-status`, {
            thread_ids: list.map(t => t.id),
          });
          setCollabById(collabRes.data?.collab || {});
        } catch (collabErr) {
          console.warn('Bulk collab fetch failed (non-fatal):', collabErr);
          setCollabById({});
        }
      } else {
        setCollabById({});
      }
    } catch (e) {
      console.error('Failed to load threads:', e);
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 503) {
        toast.error(`${detail}. Please reconnect.`);
        onDisconnect?.();
      } else if (isGmailReconnectError(e)) {
        // Refresh status so the page shows the Reconnect banner (session stays intact).
        toast.error('Gmail credentials expired. Please reconnect the mailbox.', { id: 'gmail-reconnect' });
        onDisconnect?.();
      } else if (e.response?.status === 429) {
        toast.warning(detail, { id: 'gmail-rate-limit' });
      } else {
        toast.error(`Failed to load threads: ${detail}`, { id: 'gmail-threads-error' });
      }
    } finally {
      setLoadingThreads(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, query, activeMailbox]);

  // Initial + on-filter fetch
  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Auto-poll every 60s as a fallback (realtime push catches new mail in ~30s server-side)
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      fetchThreads();
    }, 60000);
    return () => clearInterval(pollRef.current);
  }, [fetchThreads]);

  // Realtime: refresh thread list when new mail arrives in the active mailbox
  const { notify } = useDesktopNotifications();
  useRealtime({
    'email.received': (data) => {
      // Only react if the event is for the currently-viewed mailbox type.
      const isPersonalEvent = data?.mailbox_type === 'personal_mailbox';
      const matchesActive =
        (isPersonalEvent && activeMailbox === 'personal') ||
        (!isPersonalEvent && activeMailbox === 'shared');
      if (matchesActive) {
        fetchThreads();
      }
      const count = data?.count || 1;
      notify(
        count === 1 ? 'New email' : `${count} new emails`,
        {
          body: data?.mailbox_email ? `In ${data.mailbox_email}` : '',
        }
      );
    },
  });

  const openThread = useCallback(async (threadId) => {
    setSelectedId(threadId);
    setLoadingThread(true);
    setThread(null);
    setReplyState({ open: false, mode: 'reply' });
    setThreadDraft(null);
    try {
      const res = await axios.get(`${API}/communications/threads/${threadId}?mailbox=${activeMailbox}`);
      setThread(res.data);
      // If a Gmail draft already exists for this thread, surface it (Gmail-style).
      (async () => {
        try {
          const dr = await axios.get(`${API}/communications/drafts/by-thread/${threadId}?mailbox=${activeMailbox}`);
          if (dr.data?.draft) {
            setThreadDraft(dr.data.draft);
            setReplyState({ open: true, mode: 'reply' });
          }
        } catch (e) { /* drafts are best-effort */ }
      })();
      // Mark as read if any message is unread
      const anyUnread = (res.data.messages || []).some(m => m.is_unread);
      if (anyUnread) {
        try {
          await axios.post(`${API}/communications/threads/${threadId}/modify?mailbox=${activeMailbox}`, {
            remove_label_ids: ['UNREAD'],
          });
          // Optimistically update the local thread list
          setThreads(prev => prev.map(t => t.id === threadId ? { ...t, is_unread: false } : t));
        } catch (modErr) {
          console.warn('Failed to mark thread read:', modErr);
        }
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Failed to load thread: ${detail}`);
    } finally {
      setLoadingThread(false);
    }
  }, [activeMailbox]);

  const onSubmitSearch = (e) => {
    e.preventDefault();
    setQuery(searchInput);
  };

  const downloadAttachment = useCallback(async (messageId, att) => {
    try {
      const url = `${API}/communications/attachments/${messageId}/${att.attachment_id}?mailbox=${activeMailbox}&filename=${encodeURIComponent(att.filename)}`;
      const token = localStorage.getItem('token');
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  }, [activeMailbox]);

  const archiveThread = async () => {
    if (!thread) return;
    setActing(true);
    try {
      await axios.post(`${API}/communications/threads/${thread.id}/modify?mailbox=${activeMailbox}`, {
        remove_label_ids: ['INBOX'],
      });
      toast.success('Thread archived');
      setThreads(prev => prev.filter(t => t.id !== thread.id));
      setThread(null);
      setSelectedId(null);
    } catch (e) {
      toast.error(`Archive failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setActing(false);
    }
  };

  const trashThread = async () => {
    if (!thread) return;
    if (!window.confirm('Move this thread to Trash?')) return;
    setActing(true);
    try {
      await axios.post(`${API}/communications/threads/${thread.id}/trash?mailbox=${activeMailbox}`);
      toast.success('Thread moved to trash');
      setThreads(prev => prev.filter(t => t.id !== thread.id));
      setThread(null);
      setSelectedId(null);
    } catch (e) {
      toast.error(`Trash failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setActing(false);
    }
  };

  const toggleUnread = async () => {
    if (!thread) return;
    const anyUnread = (thread.messages || []).some(m => m.is_unread);
    setActing(true);
    try {
      await axios.post(`${API}/communications/threads/${thread.id}/modify?mailbox=${activeMailbox}`, {
        [anyUnread ? 'remove_label_ids' : 'add_label_ids']: ['UNREAD'],
      });
      toast.success(anyUnread ? 'Marked as read' : 'Marked as unread');
      setThreads(prev => prev.map(t => t.id === thread.id ? { ...t, is_unread: !anyUnread } : t));
      // Reflect in the open thread too
      setThread(t => t ? {
        ...t,
        messages: t.messages.map(m => ({ ...m, is_unread: !anyUnread })),
      } : t);
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setActing(false);
    }
  };

  // Apply / remove labels (used by drag-drop AND the Labels menu)
  const applyLabelChanges = useCallback(async (threadId, { add = [], remove = [] }) => {
    if (!threadId) return;
    try {
      await axios.post(
        `${API}/communications/threads/${threadId}/modify?mailbox=${activeMailbox}`,
        {
          add_label_ids: add.length ? add : undefined,
          remove_label_ids: remove.length ? remove : undefined,
        }
      );
      if (selectedId === threadId) {
        try {
          const res = await axios.get(`${API}/communications/threads/${threadId}?mailbox=${activeMailbox}`);
          setThread(res.data);
        } catch {}
      }
      fetchThreads();
    } catch (e) {
      toast.error(`Label update failed: ${e.response?.data?.detail || e.message}`);
    }
  }, [activeMailbox, selectedId, fetchThreads]);

  // Toggle a label on the currently open thread
  const toggleLabelOnThread = async (labelId) => {
    if (!thread) return;
    const has = (thread.messages || []).some(m => (m.label_ids || []).includes(labelId));
    await applyLabelChanges(thread.id, has ? { remove: [labelId] } : { add: [labelId] });
    toast.success(has ? 'Label removed' : 'Label added');
  };

  // Drag-drop: drop a thread onto a folder/label
  const handleDropOnFolder = async (targetId) => {
    const threadId = window.__dragThreadId;
    window.__dragThreadId = null;
    setDragOverFolder(null);
    if (!threadId || !targetId) return;
    // Folder drops: INBOX/STARRED/TRASH add the label; SENT/DRAFT can't be set
    if (targetId === 'TRASH') {
      try {
        await axios.post(`${API}/communications/threads/${threadId}/trash?mailbox=${activeMailbox}`);
        toast.success('Moved to trash');
        setThreads(prev => prev.filter(t => t.id !== threadId));
        if (selectedId === threadId) { setSelectedId(null); setThread(null); }
      } catch (e) {
        toast.error(`Move failed: ${e.response?.data?.detail || e.message}`);
      }
      return;
    }
    if (targetId === 'INBOX' || targetId === 'STARRED') {
      await applyLabelChanges(threadId, { add: [targetId] });
      toast.success(`Added to ${targetId === 'INBOX' ? 'Inbox' : 'Starred'}`);
      return;
    }
    // User-defined label → MOVE it (Gmail-style): apply the label and remove from Inbox.
    await applyLabelChanges(threadId, { add: [targetId], remove: ['INBOX'] });
    const name = labels.find(l => l.id === targetId)?.name || 'label';
    setThreads(prev => prev.filter(t => t.id !== threadId));
    if (selectedId === threadId) { setSelectedId(null); setThread(null); }
    toast.success(`Moved to "${name}"`);
  };

  // --- Resizable / collapsible panel controls -------------------------------
  const railPanelRef = useRef(null);
  const teamPanelRef = useRef(null);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [teamCollapsed, setTeamCollapsed] = useState(false);
  const collabActive = showCollab && !!thread;

  const toggleRail = () => {
    const p = railPanelRef.current;
    if (!p) return;
    p.isCollapsed() ? p.expand() : p.collapse();
  };
  const toggleTeam = () => {
    const p = teamPanelRef.current;
    if (!p) return;
    p.isCollapsed() ? p.expand() : p.collapse();
  };

  // The panel group remounts when the team panel appears/disappears. We keep the
  // collapsed flags in sync with the REAL rendered layout via onLayout (below),
  // which fires on mount/restore and every resize — so a restored-collapsed panel
  // always shows its floating expand button (no more "stuck collapsed" trap).
  const syncCollapsedFromLayout = (sizes) => {
    setRailCollapsed((sizes[0] ?? 100) < 1);
    setTeamCollapsed(collabActive ? ((sizes[sizes.length - 1] ?? 100) < 1) : false);
  };

  return (
    <div
      className="relative h-[calc(100vh-220px)] min-h-[600px] bg-[#F6F8FC] rounded-2xl p-2"
      data-testid="communications-inbox"
      style={{ fontFamily: '"Google Sans", Roboto, Arial, sans-serif' }}
    >
      {/* Floating expand buttons when a side panel is collapsed */}
      {railCollapsed && (
        <Button
          variant="outline"
          size="sm"
          onClick={toggleRail}
          className="hidden md:flex absolute left-0 top-1 z-30 h-8 w-8 p-0 shadow-sm"
          title="Show folders"
          data-testid="expand-rail-btn"
        >
          <PanelLeftOpen size={16} />
        </Button>
      )}
      {collabActive && teamCollapsed && (
        <Button
          variant="outline"
          size="sm"
          onClick={toggleTeam}
          className="hidden md:flex absolute right-0 top-1 z-30 h-8 w-8 p-0 shadow-sm"
          title="Show team panel"
          data-testid="expand-team-btn"
        >
          <PanelRightOpen size={16} />
        </Button>
      )}

      <ResizablePanelGroup
        key={collabActive ? 'with-team' : 'no-team'}
        direction="horizontal"
        autoSaveId={collabActive ? 'comms-inbox-team' : 'comms-inbox'}
        onLayout={syncCollapsedFromLayout}
        className="gap-0"
      >
        {/* Folder rail */}
        <ResizablePanel
          ref={railPanelRef}
          collapsible
          collapsedSize={0}
          minSize={10}
          maxSize={22}
          defaultSize={14}
          onCollapse={() => setRailCollapsed(true)}
          onExpand={() => setRailCollapsed(false)}
          className="hidden md:block"
        >
          {/* Folder rail */}
      <aside className="h-full bg-[#F6F8FC] p-2 space-y-1 overflow-y-auto">
        <div className="flex justify-end -mb-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleRail}
            className="h-6 w-6 p-0 text-[#5f6368]"
            title="Hide folders"
            data-testid="collapse-rail-btn"
          >
            <PanelLeftClose size={14} />
          </Button>
        </div>
        <button
          onClick={() => openCompose('new')}
          className="flex items-center gap-3 h-14 pl-4 pr-6 mb-3 rounded-2xl bg-[#C2E7FF] text-[#001D35] hover:shadow-md transition-shadow text-sm font-medium"
          data-testid="compose-new-btn"
        >
          <Plus size={20} />
          Compose
        </button>

        {mailboxes.length > 1 && (
          <div className="px-1 pb-2 mb-1 border-b">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2 mb-1">Mailbox</p>
            {mailboxes.map(mb => (
              <button
                key={mb.id}
                onClick={() => {
                  setActiveMailbox(mb.id);
                  setSelectedId(null);
                  setThread(null);
                }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${
                  activeMailbox === mb.id ? 'bg-secondary text-secondary-foreground font-semibold' : 'text-muted-foreground hover:bg-muted'
                }`}
                data-testid={`mailbox-switch-${mb.id}`}
                title={mb.email || mb.label}
              >
                <Mail size={12} />
                <span className="truncate">{mb.label}</span>
              </button>
            ))}
          </div>
        )}

        {FOLDERS.map(f => {
          const Icon = f.icon;
          const active = folder === f.id;
          const droppable = ['INBOX', 'STARRED', 'TRASH'].includes(f.id);
          const isDragOver = dragOverFolder === f.id;
          return (
            <button
              key={f.id}
              onClick={() => { setFolder(f.id); setSelectedId(null); setThread(null); }}
              onDragOver={droppable ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolder(f.id); } : undefined}
              onDragLeave={droppable ? () => setDragOverFolder(null) : undefined}
              onDrop={droppable ? (e) => { e.preventDefault(); handleDropOnFolder(f.id); } : undefined}
              data-testid={`folder-${f.id}`}
              className={`w-full flex items-center gap-4 pl-4 pr-6 py-1.5 h-9 rounded-r-full text-sm transition-colors ${
                active ? 'bg-[#D3E3FD] text-[#041E49] font-medium' : 'text-[#444746] hover:bg-black/5'
              } ${isDragOver ? 'ring-2 ring-[#0B57D0] ring-offset-1' : ''}`}
            >
              <Icon size={18} className={active ? 'text-[#0B57D0]' : ''} />
              <span>{f.label}</span>
            </button>
          );
        })}

        {/* User labels */}
        {userLabels.length > 0 && (
          <div className="pt-2 mt-2 border-t">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2 mb-1">Labels</p>
            {userLabels.map(l => {
              const active = folder === l.id;
              const isDragOver = dragOverFolder === l.id;
              return (
                <button
                  key={l.id}
                  onClick={() => { setFolder(l.id); setSelectedId(null); setThread(null); }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolder(l.id); }}
                  onDragLeave={() => setDragOverFolder(null)}
                  onDrop={(e) => { e.preventDefault(); handleDropOnFolder(l.id); }}
                  data-testid={`label-${l.id}`}
                  className={`w-full flex items-center gap-4 pl-4 pr-6 py-1.5 h-8 rounded-r-full text-sm transition-colors ${
                    active ? 'bg-[#D3E3FD] text-[#041E49] font-medium' : 'text-[#444746] hover:bg-black/5'
                  } ${isDragOver ? 'ring-2 ring-[#0B57D0] ring-offset-1' : ''}`}
                  title={l.name}
                >
                  <Tag size={16} className={active ? 'text-[#0B57D0]' : 'opacity-70'} />
                  <span className="truncate">{l.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </aside>
        </ResizablePanel>

        <ResizableHandle withHandle className="hidden md:flex mx-1.5 bg-transparent" />

        {/* Thread list */}
        <ResizablePanel minSize={16} defaultSize={collabActive ? 24 : 30}>
      <section className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
        <div className="p-3">
          <form onSubmit={onSubmitSearch} className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5f6368]" />
            <input
              type="text"
              placeholder="Search mail"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="w-full pl-11 pr-9 h-12 text-sm rounded-full bg-[#EAF1FB] border-0 focus:bg-white focus:shadow-md focus:outline-none transition-shadow"
              data-testid="inbox-search"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => { setSearchInput(''); setQuery(''); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5f6368] hover:text-[#1f1f1f]"
              >
                <X size={16} />
              </button>
            )}
          </form>
        </div>
        <div className="flex items-center justify-between px-4 py-1.5 text-xs text-[#5f6368] border-b border-gray-100">
          <span>
            {loadingThreads ? 'Loading…' : `${visibleThreads.length} thread${visibleThreads.length === 1 ? '' : 's'}`}
          </span>
          <Button variant="ghost" size="sm" onClick={() => fetchThreads()} className="h-7 px-2" data-testid="refresh-threads-btn">
            <RefreshCw size={12} className={loadingThreads ? 'animate-spin' : ''} />
          </Button>
        </div>
        {showCollab && (
          <div className="flex items-center gap-1 px-2 py-1.5 border-b overflow-x-auto" data-testid="collab-filters">
            {[
              { id: 'all', label: 'All' },
              { id: 'mine', label: 'Assigned to me' },
              { id: 'unassigned', label: 'Unassigned' },
              { id: 'open', label: 'Open' },
              { id: 'snoozed', label: 'Snoozed' },
              { id: 'resolved', label: 'Resolved' },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setCollabFilter(opt.id)}
                data-testid={`collab-filter-${opt.id}`}
                className={`px-3 py-1 rounded-full text-[11px] whitespace-nowrap transition-colors ${
                  collabFilter === opt.id
                    ? 'bg-[#D3E3FD] text-[#041E49] font-medium'
                    : 'bg-[#F1F3F4] text-[#5f6368] hover:bg-black/5'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {loadingThreads && visibleThreads.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : visibleThreads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground text-sm gap-2">
              <Mail size={24} />
              <span>No threads</span>
            </div>
          ) : (
            visibleThreads.map(t => (
              <ThreadRow
                key={t.id}
                thread={t}
                active={t.id === selectedId}
                onClick={() => openThread(t.id)}
                collab={collabById[t.id]}
              />
            ))
          )}
        </div>
      </section>
        </ResizablePanel>

        <ResizableHandle withHandle className="mx-1.5 bg-transparent" />

        {/* Thread reader */}
        <ResizablePanel minSize={22} defaultSize={collabActive ? 40 : 56}>
      <section className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
        {!selectedId ? (
          <div className="flex flex-col items-center justify-center h-full text-[#5f6368] gap-2 p-8 text-center">
            <MailOpen size={32} />
            <p className="text-sm">Select a thread to read</p>
            <p className="text-xs">
              Showing <span className="font-mono">{FOLDERS.find(f => f.id === folder)?.label || folder}</span>
              {query && <> matching "<span className="font-mono">{query}</span>"</>}
            </p>
          </div>
        ) : loadingThread ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 size={24} className="animate-spin text-muted-foreground" />
          </div>
        ) : thread ? (
          <>
            <header className="border-b border-gray-100 p-4 flex items-start gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedId(null); setThread(null); }}
                className="md:hidden -ml-2 h-8 w-8 p-0"
              >
                <ChevronLeft size={18} />
              </Button>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-normal text-[#1f1f1f] truncate" data-testid="thread-subject">
                  {thread.subject}
                </h2>
                <p className="text-xs text-[#5f6368] mt-0.5">
                  {thread.participants.length} participant{thread.participants.length === 1 ? '' : 's'} ·
                  {' '}{thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openInlineReply('reply')} title="Reply" data-testid="header-reply-btn">
                  <Reply size={16} />
                </Button>
                {thread.participants.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => openInlineReply('replyAll')} title="Reply all" data-testid="header-reply-all-btn">
                    <Users size={16} />
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => openInlineReply('forward')} title="Forward" data-testid="header-forward-btn">
                  <Forward size={16} />
                </Button>
                <div className="w-px h-5 bg-border mx-0.5" />
                <Button variant="ghost" size="sm" onClick={toggleUnread} disabled={acting} title="Toggle read/unread" data-testid="toggle-unread-btn">
                  <MailOpen size={16} />
                </Button>
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowLabelMenu(v => !v)}
                    disabled={acting || userLabels.length === 0}
                    title="Apply label"
                    data-testid="apply-label-btn"
                  >
                    <Tag size={16} />
                  </Button>
                  {showLabelMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowLabelMenu(false)} />
                      <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border z-50 py-1 max-h-64 overflow-y-auto">
                        <p className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Labels</p>
                        {userLabels.map(l => {
                          const has = (thread.messages || []).some(m => (m.label_ids || []).includes(l.id));
                          return (
                            <button
                              key={l.id}
                              onClick={async () => {
                                await toggleLabelOnThread(l.id);
                                setShowLabelMenu(false);
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted/50 flex items-center gap-2"
                              data-testid={`apply-label-${l.id}`}
                            >
                              <span className={`w-3 h-3 rounded-sm border flex items-center justify-center ${has ? 'bg-primary border-primary' : 'border-muted-foreground'}`}>
                                {has && <span className="text-white text-[8px]">✓</span>}
                              </span>
                              <span className="truncate">{l.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
                {folder !== 'TRASH' && (
                  <Button variant="ghost" size="sm" onClick={archiveThread} disabled={acting} title="Archive" data-testid="archive-thread-btn">
                    <Archive size={16} />
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={trashThread} disabled={acting} title="Move to trash" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="trash-thread-btn">
                  <Trash2 size={16} />
                </Button>
              </div>
            </header>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {[...thread.messages]
                .sort((a, b) => (b.internal_date_ms || 0) - (a.internal_date_ms || 0))
                .map(m => (
                  <MessageBlock key={m.id} message={m} onDownloadAttachment={downloadAttachment} />
                ))}
            </div>
            {/* Inline reply / forward composer (Gmail-style) */}
            {replyState.open ? (
              <InlineReplyComposer
                key={`${thread.id}-${replyState.mode}-${threadDraft?.draft_id || 'new'}`}
                mode={replyState.mode}
                replyTo={thread.messages[thread.messages.length - 1]}
                mailbox={activeMailbox}
                existingDraft={threadDraft}
                onDraftSaved={() => fetchThreads()}
                onSent={(data) => {
                  setReplyState({ open: false, mode: 'reply' });
                  setThreadDraft(null);
                  fetchThreads();
                  if (selectedId) {
                    (async () => {
                      try {
                        const res = await axios.get(`${API}/communications/threads/${selectedId}?mailbox=${activeMailbox}`);
                        setThread(res.data);
                      } catch {}
                    })();
                  }
                }}
                onClose={() => {
                  setReplyState({ open: false, mode: 'reply' });
                  setThreadDraft(null);
                  fetchThreads();
                }}
              />
            ) : (
              <footer className="border-t p-3 flex gap-2 bg-muted/20">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => openInlineReply('reply')}
                  className="gap-2"
                  data-testid="reply-btn"
                >
                  <Reply size={14} /> Reply
                </Button>
                {thread.participants.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openInlineReply('replyAll')}
                    className="gap-2"
                    data-testid="reply-all-btn"
                  >
                    <Users size={14} /> Reply All
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openInlineReply('forward')}
                  className="gap-2"
                  data-testid="forward-btn"
                >
                  <Forward size={14} /> Forward
                </Button>
              </footer>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground gap-2">
            <AlertCircle size={20} />
            <span className="text-sm">Failed to load thread</span>
          </div>
        )}
      </section>
        </ResizablePanel>

        {/* Collaboration panel — shared mailbox only, when a thread is open */}
        {collabActive && (
          <ResizableHandle withHandle className="hidden md:flex mx-1.5 bg-transparent" />
        )}
        {collabActive && (
          <ResizablePanel
            ref={teamPanelRef}
            collapsible
            collapsedSize={0}
            minSize={14}
            maxSize={34}
            defaultSize={22}
            onCollapse={() => setTeamCollapsed(true)}
            onExpand={() => setTeamCollapsed(false)}
            className="hidden md:block"
          >
            <div className="relative h-full overflow-y-auto bg-white border rounded-lg">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTeam}
                className="absolute right-1 top-1 z-10 h-6 w-6 p-0 text-muted-foreground"
                title="Hide team panel"
                data-testid="collapse-team-btn"
              >
                <PanelRightClose size={14} />
              </Button>
              <ThreadCollaboration
                threadId={thread.id}
                currentUserId={user?.id}
                currentUserRole={user?.role}
                onChange={(updated) => {
                  setCollabById(prev => ({
                    ...prev,
                    [thread.id]: {
                      assigned_to: updated.assigned_to,
                      status: updated.status,
                      snoozed_until: updated.snoozed_until,
                      note_count: (updated.notes || []).length,
                    },
                  }));
                }}
              />
            </div>
          </ResizablePanel>
        )}
      </ResizablePanelGroup>

      <ComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={(result) => {
          // Refresh thread list (and the currently open thread if it was a reply)
          fetchThreads();
          if (selectedId && result?.thread_id === selectedId) {
            // Reload the thread to show the new message
            (async () => {
              try {
                const res = await axios.get(`${API}/communications/threads/${selectedId}?mailbox=${activeMailbox}`);
                setThread(res.data);
              } catch {}
            })();
          }
        }}
        mode={composeMode}
        replyTo={composeReplyTo}
        senderEmail={activeMailboxEmail}
        mailbox={activeMailbox}
      />
    </div>
  );
}
